import { KnownId, StatusData } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { findLastReading } from "../utilities/findLastReading";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { fetchStatusDataComplete } from "@/utilities/query/fetchStatusDataComplete";

/** A last reading only changes when the vehicle reports again, and then the live window has it. */
const STALE_MS = 5 * 60 * 1000;

/**
 * The checklist lines shown even when the vehicle has not reported them in the period: an
 * installer reading off the odometer needs the last figure, and an old one is marked as such.
 *
 * A parked vehicle reports its odometer rarely, so these are often missing from any short window.
 */
export const LAST_KNOWN_DIAGNOSTIC_IDS: string[] = [
  KnownId.DiagnosticRawOdometerId,
  KnownId.DiagnosticOdometerId,
];

/**
 * The newest reading of each diagnostic sent before `before`, however old it is.
 *
 * Searched backwards in growing windows by {@link findLastReading}, because Geotab interpolates
 * odometers and refuses to sort them newest first (2026-10-27, regiobus). One to nine calls per
 * diagnostic; a vehicle that drove in the last day needs one, one that never sent it too.
 *
 * Without an odometer, the last figure entered by hand is looked up as well, for the odometer
 * line to fall back on. Those are rare, so its windows grow faster: the client holds every call
 * for 1.5 s to bundle it, and the search waits for each before the next.
 *
 * @param before - Start of the period the page shows; now where left out.
 */
export function useLastKnownStatusData(
  deviceId: string | undefined,
  diagnosticIds: string[],
  before?: Date,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery({
    queryKey: ["lastKnownStatusData", activeSessionId, deviceId, diagnosticIds, before?.getTime()],
    queryFn: async ({ signal }) => {
      const until = before ?? new Date();
      const lastOf = (diagnosticId: string, growth?: number) =>
        findLastReading(
          async (fromDate, toDate) => {
            const rows: StatusData[] = [];
            await fetchStatusDataComplete(geotabClient, {
              tasks: [{ key: diagnosticId, deviceId: deviceId as string, diagnosticId }],
              fromDate,
              toDate,
              signal,
              onPage: (_key, page) => rows.push(...page),
            });
            return rows;
          },
          until,
          { growth },
        );

      const found = (await Promise.all(diagnosticIds.map(id => lastOf(id)))).filter(
        (row): row is StatusData => row !== undefined,
      );
      if (found.some(row => row.diagnostic?.id === KnownId.DiagnosticOdometerId)) {
        return found;
      }
      const entered = await lastOf(KnownId.DiagnosticOdometerAdjustmentId, 4);
      return entered ? [...found, entered] : found;
    },
    enabled: !!deviceId && diagnosticIds.length > 0,
    staleTime: STALE_MS,
    // No placeholder: the previous vehicle's odometer would stand under the new one's name.
  });
}
